import { ref } from 'vue';
import { useAppState } from './useAppState';
import { useSpending } from './useSpending';
import { useSpendingPlans } from './useSpendingPlans';
export function useSpendingPlansSidebar(registerSidebar = null) {
    const state = useAppState();
    const spending = useSpending();
    const { extractActivePlans } = useSpendingPlans();
    const spendingPlansSidebarItems = ref([]);
    const selectedPlanId = ref(null);
    function fetchAndRegisterSpendingPlans(selectedSlotKey = null, handlers) {
        spending.getSpendingPlansFull(() => {
            state.spendingPlans = extractActivePlans(state.spendingPlansFull)?.plans || [];
            buildSpendingPlansSidebarItems(selectedSlotKey);
            registerSpendingPlansSidebar(handlers);
        });
    }
    function buildSpendingPlansSidebarItems(selectedSlotKey = null) {
        const items = [];
        const plans = state.spendingPlans?.plans || state.spendingPlans || [];
        const activePlans = plans.filter((p) => p.plan?.amount > 0);
        if (!activePlans.length || !state.spendingAnalysisStructured?.grandparentAnalysis) {
            spendingPlansSidebarItems.value = items;
            return;
        }
        // Get spending data for the selected slot
        const slotAnalysis = state.spendingAnalysisStructured.grandparentAnalysis
            .filter((a) => a.slotName === selectedSlotKey);
        // Collect all grouped analysis items
        const allGroups = [];
        slotAnalysis.forEach((a) => {
            if (a.groupedAnalysis) {
                a.groupedAnalysis.forEach((g) => allGroups.push(g));
            }
        });
        // Build sidebar items
        activePlans.forEach((plan) => {
            const classId = plan.classification?.id;
            const budget = plan.plan?.amount || 0;
            if (!budget)
                return;
            // Find matching spending from analysis
            const group = allGroups.find((g) => String(g.transactionClassification?.id) === String(classId));
            const spent = group ? Math.abs(group.amountNet || 0) : 0;
            const percent = Math.min(100, Math.round((spent / budget) * 100));
            items.push({
                id: `plan-${classId}`,
                title: plan.classification?.fullName || 'Plan',
                subtitle: `${state.formatter.format(spent)} / ${state.formatter.format(budget)}`,
                progress: percent,
                colour: percent >= 100 ? '#c62828' : percent >= 80 ? '#f9a825' : '#2e7d32',
                // Store extra data for click handler
                _planData: {
                    classificationId: classId,
                    name: plan.classification?.fullName,
                    iconPath: plan.classification?.iconPath,
                    budget: budget,
                    spent: spent,
                    remaining: budget - spent,
                    percent: percent
                }
            });
        });
        spendingPlansSidebarItems.value = items;
    }
    function registerSpendingPlansSidebar(handlers) {
        if (!registerSidebar)
            return;
        registerSidebar({
            config: {
                actionButton: null,
                pageItemsTitle: 'Spending Plans',
                pageItemsEmptyText: 'No plans set',
                pageItems: spendingPlansSidebarItems.value,
                currentPageItemId: selectedPlanId.value
            },
            handlers: {
                onActionClick: () => { },
                onPageItemClick: (id) => { handleSpendingPlanClick(id, handlers?.onSpendingPlanSelected); },
                onPageItemDelete: () => { },
                onPageItemRename: () => { },
                onOpenSettings: () => { if (handlers?.onOpenSettings)
                    handlers.onOpenSettings(); }
            }
        });
    }
    function handleSpendingPlanClick(planId, onSpendingPlanSelected) {
        if (!String(planId).startsWith('plan-'))
            return;
        const item = spendingPlansSidebarItems.value.find((i) => i.id === planId);
        if (!item)
            return;
        selectedPlanId.value = planId;
        // Call the page's handler if it exists
        if (typeof onSpendingPlanSelected === 'function') {
            onSpendingPlanSelected(item._planData);
        }
        registerSpendingPlansSidebar({ onSpendingPlanSelected });
    }
    function clearSpendingPlanSelection() {
        selectedPlanId.value = null;
        registerSpendingPlansSidebar();
    }
    function getSpendingPlanData(planId) {
        const item = spendingPlansSidebarItems.value.find((i) => i.id === planId);
        return item?._planData || null;
    }
    return {
        spendingPlansSidebarItems,
        selectedPlanId,
        fetchAndRegisterSpendingPlans,
        buildSpendingPlansSidebarItems,
        registerSpendingPlansSidebar,
        handleSpendingPlanClick,
        clearSpendingPlanSelection,
        getSpendingPlanData,
    };
}
